import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, CheckCircle2, ExternalLink } from "lucide-react";
import PageGlow from "@/components/PageGlow";
import TurnstileWidget from "@/components/TurnstileWidget";
import { brand } from "@/config/brand";

const fg = "hsl(var(--foreground))";
const fgMuted = "hsl(var(--muted-foreground))";
const surface = "hsl(var(--card))";
const border = "hsl(var(--border))";
const GRAD = "#5048E5";
const FONT = "Plus Jakarta Sans, sans-serif";
const HEAD = "Outfit, sans-serif";

const INPUT =
  "w-full rounded-xl px-4 py-3 text-sm outline-none transition-colors focus:ring-2";
const inputStyle: React.CSSProperties = {
  background: surface,
  border: `1px solid ${border}`,
  color: fg,
  fontFamily: FONT,
};

type SubmitResult = {
  success?: boolean;
  challenge?: boolean;
  siteKey?: string;
  error?: string;
};

export default function DeleteAccountPage() {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [challengeSiteKey, setChallengeSiteKey] = useState<string | null>(null);

  async function submit(turnstileToken?: string) {
    setSending(true);
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Account deletion request",
          email: email.trim(),
          service: "Account deletion request",
          message:
            "Account deletion request submitted via the public /delete-account page. Please delete the account associated with the email above and any related personal data, per section 10 of the privacy policy.",
          website, // honeypot
          turnstileToken,
        }),
      });
      const data = (await res.json()) as SubmitResult;

      if (data.success) {
        setSent(true);
        setChallengeSiteKey(null);
        return;
      }
      if (data.challenge && data.siteKey) {
        setChallengeSiteKey(data.siteKey);
        setError(data.error ?? "");
        return;
      }
      setError(data.error ?? "Something went wrong — please try again.");
    } catch {
      setError("Could not send your request — please try again.");
    } finally {
      setSending(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    void submit();
  }

  return (
    <>
      {/* Hero */}
      <section
        className="relative overflow-hidden py-10 md:py-14"
        style={{ background: "hsl(var(--background))" }}
      >
        <PageGlow tall />
        <div className="relative mx-auto max-w-3xl px-6 text-center">


          <h1
            className="mb-4 text-4xl font-bold md:text-5xl"
            style={{ fontFamily: HEAD, color: fg, lineHeight: 1.06 }}
          >
            Delete your account
          </h1>
          <p
            className="text-sm"
            style={{ fontFamily: FONT, color: fgMuted }}
          >
            Two ways to remove your {brand.name} account and personal data.
          </p>
        </div>
      </section>

      {/* Content */}
      <section
        className="pb-24"
        style={{ background: "hsl(var(--background))" }}
      >
        <div className="mx-auto max-w-3xl px-6">
          {/* What deletion does */}
          <div
            className="mb-8 rounded-2xl p-6"
            style={{ background: surface, border: `1px solid ${border}` }}
          >
            <h2
              className="mb-3 text-lg font-bold"
              style={{ fontFamily: HEAD, color: fg }}
            >
              What deletion removes
            </h2>
            <p
              className="mb-3 text-sm leading-relaxed"
              style={{ fontFamily: FONT, color: fgMuted }}
            >
              We will remove or anonymise your profile information, chat
              messages and voice notes, uploaded files, call session history,
              invoice and agreement records, and push device tokens. Some data
              may be retained where required by law (for example, financial
              records for tax purposes) — where that applies, we keep it only
              for the minimum period required and tell you what we're keeping
              and why.
            </p>
            <p
              className="text-sm leading-relaxed"
              style={{ fontFamily: FONT, color: fgMuted }}
            >
              We confirm receipt of every request within{" "}
              <strong style={{ color: fg }}>48 hours</strong> and complete the
              deletion within{" "}
              <strong style={{ color: fg }}>30 days</strong>. For the full
              details, see{" "}
              <Link
                to="/privacy-policy"
                hash="section-10"
                className="underline"
                style={{ color: GRAD }}
              >
                section 10 of our privacy policy
              </Link>
              .
            </p>
          </div>

          {/* Route A — signed in */}
          <div
            className="mb-6 rounded-2xl p-6"
            style={{ background: surface, border: `1px solid ${border}` }}
          >
            <h2
              className="mb-3 text-lg font-bold"
              style={{ fontFamily: HEAD, color: fg }}
            >
              If you can sign in
            </h2>
            <p
              className="mb-4 text-sm leading-relaxed"
              style={{ fontFamily: FONT, color: fgMuted }}
            >
              Sign in and open your profile — scroll to the bottom and choose{" "}
              <strong style={{ color: fg }}>Delete my account</strong>. Your
              account and data are removed straight away.
            </p>
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90"
              style={{ background: GRAD, fontFamily: FONT }}
            >
              Go to your profile
              <ExternalLink size={14} />
            </Link>
          </div>

          {/* Route B — request form */}
          <div
            className="rounded-2xl p-6"
            style={{ background: surface, border: `1px solid ${border}` }}
          >
            <h2
              className="mb-3 text-lg font-bold"
              style={{ fontFamily: HEAD, color: fg }}
            >
              If you can't sign in
            </h2>
            <p
              className="mb-5 text-sm leading-relaxed"
              style={{ fontFamily: FONT, color: fgMuted }}
            >
              Enter the email address on your account and we'll take it from
              there. We'll confirm receipt within 48 hours and complete the
              deletion within 30 days.
            </p>

            {sent ? (
              <div
                className="flex flex-col items-center gap-3 rounded-xl px-6 py-8 text-center"
                style={{
                  border: `1px solid ${border}`,
                  background: "hsl(var(--background))",
                }}
              >
                <CheckCircle2 size={36} style={{ color: GRAD }} />
                <p
                  className="text-base font-bold"
                  style={{ color: fg, fontFamily: HEAD }}
                >
                  Request received
                </p>
                <p
                  className="text-sm"
                  style={{ color: fgMuted, fontFamily: FONT }}
                >
                  We'll confirm by email within 48 hours and complete the
                  deletion within 30 days.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* Honeypot */}
                <div
                  aria-hidden="true"
                  className="absolute h-0 w-0 overflow-hidden"
                >
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      name="website"
                    />
                  </label>
                </div>

                <label
                  className="text-xs font-semibold uppercase tracking-wider"
                  style={{ color: fgMuted, fontFamily: FONT }}
                  htmlFor="delete-account-email"
                >
                  Account email
                </label>
                <input
                  id="delete-account-email"
                  className={INPUT}
                  style={inputStyle}
                  placeholder="you@example.com"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={255}
                  required
                />

                {challengeSiteKey && (
                  <div
                    className="flex flex-col gap-2 rounded-xl px-4 py-4"
                    style={{
                      border: `1px solid ${border}`,
                      background: "hsl(var(--background))",
                    }}
                  >
                    <p
                      className="flex items-center justify-center gap-2 text-center text-sm"
                      style={{ color: fgMuted, fontFamily: FONT }}
                    >
                      <ShieldCheck size={15} style={{ color: GRAD }} />
                      Just checking you're human — one tap and you're through.
                    </p>
                    <TurnstileWidget
                      siteKey={challengeSiteKey}
                      onVerify={(token) => void submit(token)}
                      onError={() =>
                        setError(
                          "Verification failed to load — please try again.",
                        )
                      }
                    />
                  </div>
                )}

                {error && (
                  <p
                    className="text-sm"
                    style={{ color: "hsl(14 100% 57%)", fontFamily: FONT }}
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ background: GRAD, fontFamily: FONT }}
                >
                  {sending ? "Sending…" : "Confirm deletion request"}
                </button>
              </form>
            )}
          </div>

          <p
            className="mt-8 text-center text-xs"
            style={{ color: fgMuted, fontFamily: FONT }}
          >
            Prefer email? Write to{" "}
            <a
              href={`mailto:${brand.contact.email}?subject=Account%20deletion%20request`}
              className="underline"
              style={{ color: GRAD }}
            >
              {brand.contact.email}
            </a>{" "}
            with the subject "Account deletion request".
          </p>
        </div>
      </section>
    </>
  );
}
